// Provenance: repo:tds-reconciliation-portal:frontend/src/components/{CarriedCorrectionsNote,
// CarriedVerdictsNote}.tsx (ADR-0005) — one module, restyled (ADR-0010). Carrying work
// silently would be almost as bad as losing it silently: the accountant needs to know which
// edits/verdicts are now on the new file, and which could NOT be placed.
import { ArrowLeftRight } from 'lucide-react'

import type { CarriedCorrections, CarriedVerdicts } from '../api/types'

function NoteShell({ warn, title, children }: {
  warn: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <div
      className={`mt-2 rounded-md border p-2.5 text-xs ${
        warn
          ? 'border-orange-300 bg-orange-50 text-orange-900'
          : 'border-teal-300 bg-teal-50 text-teal-900'
      }`}
    >
      <p className="mb-1 flex items-center gap-1.5 font-semibold">
        <ArrowLeftRight size={14} /> {title}
      </p>
      {children}
    </div>
  )
}

/** What happened to the previous file's corrections when this one replaced it. */
export function CarriedCorrectionsNote({ note }: { note?: CarriedCorrections }) {
  if (!note) return null
  const dropped = note.dropped ?? []
  return (
    <NoteShell
      warn={dropped.length > 0}
      title={
        dropped.length
          ? `${note.carried} correction(s) carried over, ${dropped.length} could not be placed`
          : `${note.carried} correction(s) carried over from the previous file`
      }
    >
      <p>
        Carried from <b>{note.from_filename}</b> by matching each row's own date,
        document number, payor and amount — never by its position, which shifts
        whenever a posting is added.
      </p>
      {dropped.length > 0 && (
        <>
          <p className="mt-1.5 font-semibold">
            Not carried — check whether these rows were removed on purpose:
          </p>
          <ul className="mt-1 list-disc pl-4">
            {dropped.map((d, i) => (
              <li key={i}>
                {d.date ?? '—'} · {d.docno ?? '—'} · {d.payor ?? '—'}
                {d.amount ? ` · ₹${d.amount}` : ''} — {d.field} was to become{' '}
                <b>{d.new_value}</b> ({d.why})
              </li>
            ))}
          </ul>
        </>
      )}
    </NoteShell>
  )
}

/** The mirror for duplicate verdicts: approved verdicts carry by row content;
 *  a pending (unchecked) or unplaceable one is reported, never silent. */
export function CarriedVerdictsNote({ note }: { note?: CarriedVerdicts }) {
  if (!note) return null
  const dropped = note.dropped ?? []
  return (
    <NoteShell
      warn={dropped.length > 0}
      title={
        dropped.length
          ? `${note.carried} duplicate verdict(s) carried over, ${dropped.length} must be re-judged`
          : `${note.carried} duplicate verdict(s) carried over from the previous file`
      }
    >
      <p>
        Carried from <b>{note.from_filename}</b> with their original recorder and
        checker — approved verdicts only, matched by each row's own content.
      </p>
      {dropped.length > 0 && (
        <>
          <p className="mt-1.5 font-semibold">Not carried — these groups are outstanding again:</p>
          <ul className="mt-1 list-disc pl-4">
            {dropped.map((d, i) => (
              <li key={i}>
                {d.date ?? '—'} · {d.docno ?? '—'} · {d.payor ?? '—'}
                {d.amount ? ` · ₹${d.amount}` : ''} — was <b>{d.verdict}</b> ({d.why})
              </li>
            ))}
          </ul>
        </>
      )}
    </NoteShell>
  )
}
